import { SettingOutlined, UserOutlined } from '@ant-design/icons';
import { useLogoutMenuItem } from '@framework/auth/components/logout/useLogoutMenuItem.tsx';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { useHistory } from '@framework/ui/core/router/TypedRoute.tsx';
import { type ItemType } from 'antd/es/menu/interface';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { queryClient } from '../../core/network/queryClient.ts';
import { ThemeLogic } from '../theme/ThemeLogic.ts';

/**
 * Ritorna gli elementi del menu utente
 */
export function useUserMenuItems(): { items: ItemType[] } {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const history = useHistory();
  const logoutMenuItem = useLogoutMenuItem({
    onLogout: () => queryClient.clear(),
  });
  const profileMenuItem = useMemo<ItemType>(
    () => ({
      key: 'profile-item-menu',
      icon: <UserOutlined />,
      label: t('frontoffice.menu.profile.label', 'Profilo'),
      title: t('frontoffice.menu.profile.title', 'Accedi al Profilo'),
      onClick: () => {
        history.push('/utente/profilo');
      },
    }),
    [t, history],
  );

  const settingsMenuItem = useMemo<ItemType>(
    () => ({
      key: 'settings-item-menu',
      icon: <SettingOutlined />,
      label: t('frontoffice.menu.settings.label', 'Impostazioni'),
      title: t('frontoffice.menu.settings.title', 'Accedi alle Impostazioni'),
      onClick: () => {
        history.push('/utente/impostazioni');
      },
    }),
    [t, history],
  );

  const menu = useMemo(() => {
    const result = [];
    if (ThemeLogic.allowExtendedUserLoginRoutes()) {
      result.push(profileMenuItem);
    }
    if (process.env.ENABLE_COMMUNICATION_PREFERENCES === 'true') {
      result.push(settingsMenuItem);
    }
    result.push(logoutMenuItem);
    return { items: result };
  }, [logoutMenuItem, profileMenuItem, settingsMenuItem]);

  return currentUser ? menu : { items: [] };
}
